// Field/table config for the generic CMS ResourceManager.
// types: text | textarea | select | color | image | number

const FORMAT = { name: "format", label: "Format", type: "select", options: ["in-person", "virtual"] };
const STATUS = { name: "status", label: "Status", type: "select", options: ["published", "draft", "featured"] };
const ACCENT = { name: "accent", label: "Accent color", type: "color" };
const ORDER = { name: "order", label: "Order", type: "number" };

export const SCHEMAS = {
  events: {
    label: "Events", singular: "Event", icon: "CalendarDays",
    columns: ["title", "season", "format", "date", "status"],
    fields: [
      { name: "title", label: "Title", type: "text", required: true },
      { name: "slug", label: "Slug", type: "text" },
      { name: "season", label: "Season tag", type: "text" },
      FORMAT,
      { name: "date", label: "Date(s)", type: "text" },
      { name: "time", label: "Time", type: "text" },
      { name: "location", label: "Location", type: "text" },
      { name: "audience", label: "Audience", type: "text" },
      { name: "capacity", label: "Capacity (0 = unlimited)", type: "number" },
      { name: "image", label: "Image", type: "image" },
      ACCENT,
      { name: "blurb", label: "Blurb", type: "textarea" },
      { name: "title_fr", label: "Title (FR)", type: "text" },
      { name: "blurb_fr", label: "Blurb (FR)", type: "textarea" },
      { name: "season_fr", label: "Season tag (FR)", type: "text" },
      { name: "audience_fr", label: "Audience (FR)", type: "text" },
      { name: "cta_primary_label", label: "CTA label", type: "text" },
      { name: "cta_primary_link", label: "CTA link", type: "text" },
      { name: "cta_secondary_label", label: "Secondary CTA label", type: "text" },
      { name: "cta_secondary_link", label: "Secondary CTA link", type: "text" },
      STATUS, ORDER,
    ],
  },
  initiatives: {
    label: "Initiatives", singular: "Initiative", icon: "Sparkles",
    card: { title: "name", subtitle: "tag", accent: "accent" },
    columns: ["name", "tag", "icon", "status"],
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "tag", label: "Tag", type: "text" },
      { name: "icon", label: "Icon", type: "select", options: ["Snowflake", "Sun", "BookOpen", "Users", "Wifi", "CalendarDays"] },
      ACCENT,
      { name: "blurb", label: "Blurb", type: "textarea" },
      { name: "name_fr", label: "Name (FR)", type: "text" },
      { name: "blurb_fr", label: "Blurb (FR)", type: "textarea" },
      { name: "cta_label", label: "CTA label", type: "text" },
      { name: "cta_link", label: "CTA link", type: "text" },
      STATUS, ORDER,
    ],
  },
  books: {
    label: "Benni Books", singular: "Book", icon: "BookOpen",
    card: { image: "cover", eyebrow: "number", title: "title", subtitle: "availability", accent: "accent" },
    columns: ["number", "title", "availability", "status"],
    fields: [
      { name: "number", label: "Number", type: "text" },
      { name: "title", label: "Title", type: "text", required: true },
      { name: "cover", label: "Cover image", type: "image" },
      { name: "availability", label: "Availability", type: "text" },
      { name: "ages", label: "Ages", type: "text" },
      { name: "kit", label: "Kit note", type: "text" },
      ACCENT,
      { name: "blurb", label: "Blurb", type: "textarea" },
      { name: "title_fr", label: "Title (FR)", type: "text" },
      { name: "blurb_fr", label: "Blurb (FR)", type: "textarea" },
      { name: "availability_fr", label: "Availability (FR)", type: "text" },
      { name: "ages_fr", label: "Ages (FR)", type: "text" },
      { name: "kit_fr", label: "Kit note (FR)", type: "text" },
      STATUS, ORDER,
    ],
  },
  panels: {
    label: "Comic Panels", singular: "Panel", icon: "Image",
    columns: ["title", "caption", "status"],
    fields: [
      { name: "title", label: "Title", type: "text", required: true },
      { name: "image", label: "Image", type: "image" },
      { name: "caption", label: "Caption", type: "textarea" },
      STATUS, ORDER,
    ],
  },
  partners: {
    label: "Partners", singular: "Partner", icon: "Users",
    columns: ["name", "status"],
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "logo", label: "Logo image", type: "image" },
      STATUS, ORDER,
    ],
  },
  board: {
    label: "Advisory Board", singular: "Member", icon: "Users",
    card: { image: "image", title: "name", subtitle: "role", accent: "accent" },
    columns: ["name", "role", "status"],
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "role", label: "Role / specialty", type: "text" },
      { name: "image", label: "Portrait image", type: "image" },
      ACCENT,
      { name: "bio", label: "Bio", type: "textarea" },
      { name: "role_fr", label: "Role (FR)", type: "text" },
      { name: "bio_fr", label: "Bio (FR)", type: "textarea" },
      STATUS, ORDER,
    ],
  },
  timeline: {
    label: "Timeline", singular: "Entry", icon: "CalendarDays",
    columns: ["year", "title", "status"],
    fields: [
      { name: "year", label: "Year", type: "text", required: true },
      { name: "title", label: "Title", type: "text" },
      ACCENT,
      { name: "blurb", label: "Blurb", type: "textarea" },
      STATUS, ORDER,
    ],
  },
  stats: {
    label: "Stats", singular: "Stat", icon: "Sparkles",
    columns: ["key", "value", "label"],
    fields: [
      { name: "key", label: "Key", type: "text", required: true },
      { name: "value", label: "Value", type: "text", required: true },
      { name: "label", label: "Label", type: "text" },
      STATUS, ORDER,
    ],
  },
  quotes: {
    label: "Quotes", singular: "Quote", icon: "Sparkles",
    columns: ["attribution", "text"],
    fields: [
      { name: "text", label: "Quote", type: "textarea", required: true },
      { name: "attribution", label: "Attribution", type: "text" },
      { name: "context", label: "Context", type: "text" },
      { name: "text_fr", label: "Quote (FR)", type: "textarea" },
      STATUS, ORDER,
    ],
  },
  campaigns: {
    label: "Campaigns", singular: "Campaign", icon: "Snowflake",
    columns: ["name", "timing", "status"],
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "icon", label: "Icon", type: "select", options: ["Snowflake", "Sun"] },
      { name: "timing", label: "Timing", type: "text" },
      { name: "image", label: "Image", type: "image" },
      ACCENT,
      { name: "blurb", label: "Blurb", type: "textarea" },
      { name: "name_fr", label: "Name (FR)", type: "text" },
      { name: "blurb_fr", label: "Blurb (FR)", type: "textarea" },
      { name: "cta_label", label: "CTA label", type: "text" },
      { name: "cta_link", label: "CTA link", type: "text" },
      STATUS, ORDER,
    ],
  },
  gallery: {
    label: "Gallery", singular: "Image", icon: "Image",
    columns: ["alt", "status"],
    fields: [
      { name: "url", label: "Image", type: "image", required: true },
      { name: "alt", label: "Alt text", type: "text", required: true },
      STATUS, ORDER,
    ],
  },
  participate: {
    label: "Participate Cards", singular: "Card", icon: "Sparkles",
    card: { image: "image", eyebrow: "tag", title: "title", subtitle: "blurb", accent: "accent" },
    columns: ["tag", "title"],
    fields: [
      { name: "tag", label: "Tag badge", type: "text" },
      { name: "title", label: "Title", type: "text", required: true },
      { name: "blurb", label: "Blurb", type: "textarea" },
      { name: "cta", label: "CTA label", type: "text" },
      { name: "link", label: "CTA link", type: "text" },
      { name: "icon", label: "Icon", type: "select", options: ["Share2", "CalendarDays", "Users", "BookOpen", "Sparkles"] },
      ACCENT,
      { name: "image", label: "Image", type: "image" },
      { name: "tag_fr", label: "Tag (FR)", type: "text" },
      { name: "title_fr", label: "Title (FR)", type: "text" },
      { name: "blurb_fr", label: "Blurb (FR)", type: "textarea" },
      { name: "cta_fr", label: "CTA label (FR)", type: "text" },
      STATUS, ORDER,
    ],
  },
  articles: {
    label: "Blog / Articles", singular: "Article", icon: "FileText",
    columns: ["title", "author", "category", "status"],
    fields: [
      { name: "title", label: "Title", type: "text", required: true },
      { name: "slug", label: "Slug (URL)", type: "text" },
      { name: "author", label: "Author", type: "text" },
      { name: "date", label: "Date", type: "text" },
      { name: "category", label: "Category / tag", type: "text" },
      { name: "cover", label: "Cover image", type: "image" },
      ACCENT,
      { name: "excerpt", label: "Excerpt", type: "textarea" },
      { name: "body", label: "Body (rich HTML)", type: "html" },
      { name: "title_fr", label: "Title (FR)", type: "text" },
      { name: "excerpt_fr", label: "Excerpt (FR)", type: "textarea" },
      { name: "category_fr", label: "Category (FR)", type: "text" },
      { name: "body_fr", label: "Body (FR)", type: "html" },
      { name: "show_on_home", label: "Show on home page", type: "select", options: ["no", "yes"] },
      STATUS, ORDER,
    ],
  },
  pages: {
    label: "Pages", singular: "Page", icon: "LayoutDashboard",
    columns: ["key", "title"],
    fields: [
      { name: "key", label: "Page key", type: "text", required: true },
      { name: "title", label: "Title", type: "text" },
      { name: "eyebrow", label: "Eyebrow", type: "text" },
      { name: "headline_1", label: "Headline line 1", type: "text" },
      { name: "headline_2", label: "Headline line 2 (emphasis)", type: "text" },
      { name: "headline_3", label: "Headline line 3", type: "text" },
      { name: "intro", label: "Intro paragraph", type: "textarea" },
      { name: "cta_primary_label", label: "Primary CTA label", type: "text" },
      { name: "cta_primary_link", label: "Primary CTA link", type: "text" },
      { name: "cta_secondary_label", label: "Secondary CTA label", type: "text" },
      { name: "cta_secondary_link", label: "Secondary CTA link", type: "text" },
      { name: "eyebrow_fr", label: "Eyebrow (FR)", type: "text" },
      { name: "headline_1_fr", label: "Headline line 1 (FR)", type: "text" },
      { name: "headline_2_fr", label: "Headline line 2 (FR)", type: "text" },
      { name: "headline_3_fr", label: "Headline line 3 (FR)", type: "text" },
      { name: "intro_fr", label: "Intro paragraph (FR)", type: "textarea" },
      { name: "cta_primary_label_fr", label: "Primary CTA label (FR)", type: "text" },
      { name: "cta_secondary_label_fr", label: "Secondary CTA label (FR)", type: "text" },
      ORDER,
    ],
  },
};

export const NAV_GROUPS = [
  { title: "Overview", items: [
    { to: "/admin", label: "Dashboard", icon: "LayoutDashboard", end: true },
    { to: "/admin/account", label: "Account", icon: "Lock" },
  ] },
  {
    title: "Content",
    items: [
      { to: "/admin/pages", label: "Pages", icon: "LayoutDashboard" },
      { to: "/admin/sections", label: "Page Sections", icon: "LayoutList" },
      { to: "/admin/events", label: "Events", icon: "CalendarDays" },
      { to: "/admin/initiatives", label: "Initiatives", icon: "Sparkles" },
      { to: "/admin/books", label: "Benni Books", icon: "BookOpen" },
      { to: "/admin/articles", label: "Blog / Articles", icon: "FileText" },
      { to: "/admin/participate", label: "Participate Cards", icon: "Sparkles" },
      { to: "/admin/panels", label: "Comic Panels", icon: "Image" },
      { to: "/admin/campaigns", label: "Campaigns", icon: "Snowflake" },
      { to: "/admin/partners", label: "Partners", icon: "Users" },
      { to: "/admin/board", label: "Advisory Board", icon: "Users" },
      { to: "/admin/timeline", label: "Timeline", icon: "CalendarDays" },
      { to: "/admin/stats", label: "Stats", icon: "Sparkles" },
      { to: "/admin/quotes", label: "Quotes", icon: "Sparkles" },
      { to: "/admin/gallery", label: "Gallery", icon: "Image" },
      { to: "/admin/site-images", label: "Site Images", icon: "Image" },
      { to: "/admin/media", label: "Media Library", icon: "Image" },
    ],
  },
  {
    title: "Operate",
    items: [
      { to: "/admin/submissions", label: "Submissions", icon: "Inbox" },
      { to: "/admin/rsvps", label: "RSVPs", icon: "Ticket" },
      { to: "/admin/settings", label: "Site Settings", icon: "Settings" },
      { to: "/admin/email", label: "Email (SMTP)", icon: "Mail" },
      { to: "/admin/email-templates", label: "Email Templates", icon: "Mail" },
    ],
  },
];
